import { useSelector } from 'react-redux';

import { PARTNER_MODULE_ID } from 'src/components/layouts/Standard/elementIds';
import { getTopProductFeatures } from 'src/components/molecules/ProductFeatures/utils/getTopProductFeatures';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { selectAccessibility } from 'src/redux/accessibility/selectors';
import { selectOptions } from 'src/redux/basket/selectors';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectProduct } from 'src/redux/product/selectors';
import { getOptionsCostRange } from 'src/redux/product/utils/getOptionsCostRange';
import { selectProductReviews } from 'src/redux/reviews/selectors';
import { isMadeToOrder } from 'src/utils/inventoryStatus';
import type { PDPViewModel } from './types';

export const usePDPViewModel = (): PDPViewModel => {
  // Whole-state subscription mirrors useStandardLayout for now; DISC-4240
  // replaces this with narrow selectors.
  const state = useSelector((s: ReduxApplicationState) => s);
  const product = selectProduct(state);
  const reviews = selectProductReviews(state);
  const options = selectOptions(state);
  const accessibility = selectAccessibility(state);
  const offerLabelContent = state.content.offerLabel || null;

  const optionsCostRange = getOptionsCostRange(options, product.price.currency);
  const optionsAddCost =
    options.length > 0 &&
    product.specialFeatures.personalisable &&
    optionsCostRange.minRequired > 0;
  const minimumOptionsCost = optionsAddCost ? optionsCostRange.minRequired : 0;
  const priceIsMinimum = optionsCostRange.maxOptional + optionsCostRange.maxRequired > 0;

  const offerLabelAllowedForProduct =
    !offerLabelContent?.showOnlyForDiscountedProducts || product.onSale;
  const offerLabel =
    offerLabelContent && offerLabelAllowedForProduct
      ? {
          countdown: offerLabelContent.countdown,
          message: offerLabelContent.message,
          promoCode: offerLabelContent.promoCode,
          showInRed: offerLabelContent.showInRed,
        }
      : null;

  const productFeatures = getTopProductFeatures({
    allowsGiftWrap: product.allowsGiftWrap,
    productRating: reviews.averageProductRating,
    specialFeatures: product.specialFeatures,
  });

  return {
    offerLabel,
    pageLiveRegionText: accessibility.statusMessage,
    partnerMini: {
      linkHref: `#${PARTNER_MODULE_ID}`,
      location: product.partner.location,
      logoSrc: product.partner.logo,
      name: product.partner.name,
      partnerRating: state.partner.insights?.averageProductRating ?? undefined,
      partnerReviewCount: state.partner.insights?.numberOfPartnerReviews ?? undefined,
    },
    priceAndRating: {
      averageRating: reviews.averageProductRating || undefined,
      badge: product.attributes?.highlyRated ? 'mostLoved' : product.isNew ? 'new' : undefined,
      numberOfReviews: reviews.numberOfReviews || 0,
      preSalePrices: product.preSalePrices,
      price: product.price,
      priceIsMinimum,
      reviewsLinkHref: `#${REVIEWS_URL_FRAGMENT_ID}`,
      salePercentage: product.salePercentage || 0,
      salePercentageInRed: Boolean(offerLabel?.showInRed),
    },
    productFeatures:
      productFeatures.length > 0
        ? { currency: product.price.currency, minimumOptionsCost, productFeatures }
        : null,
    showMadeToOrder: isMadeToOrder(product.inventoryStatus),
    title: product.title,
  };
};
